import { useInfiniteGetItemEntriesQuery, GetItemEntriesInput } from '@graphql/generated/graphql';
import { useEffect, useMemo } from 'react';
import logger from '@utils/logger';

const useGetItemEntries = (input: GetItemEntriesInput, options?: { enabled?: boolean }) => {
  const { enabled = true } = options ?? {};
  const { ...restInput } = input;

  const { data, error, isError, isLoading, fetchNextPage, hasNextPage, refetch } = useInfiniteGetItemEntriesQuery(
    'input',
    {
      input: {
        ...restInput,
      },
    },
    {
      staleTime: 0,
      cacheTime: 0,
      refetchInterval: false,
      // disable refetchOnWindowFocus so that data is not refreshed
      // when user leave the page and come back
      refetchOnWindowFocus: false,
      enabled,
      getNextPageParam: (lastPage) => {
        const {
          getItemEntries: { paginationToken },
        } = lastPage;
        if (!paginationToken) {
          return undefined;
        }
        return {
          input: {
            ...restInput,
            paginationToken,
          },
        };
      },
      retry: false,
    },
  );

  const itemEntriesResult = useMemo(
    () => data?.pages.flatMap((page) => page.getItemEntries.items ?? []),
    [data?.pages],
  );

  useEffect(() => {
    if (isError) {
      const { message, name } = error as Error;
      void logger.error(`Error occurred while retrieving item entries: ${name}, ${message}`);
    }
  }, [isError, error]);

  return {
    isLoading,
    itemEntriesResult,
    hasNextPage,
    fetchNextPage,
    refetch,
  };
};

export default useGetItemEntries;
